import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
    BookOpenText,
    Building2,
    Handshake,
    HeartPulse,
    MousePointerClick,
    ShieldCheck,
    Stethoscope,
    type LucideIcon,
} from "lucide-react";
import { DataKeys } from "../../types";
import ecosystemDeliveringCareImg from "../../image/health/ecosystem-delivering-care.png";
import ecosystemManagingOrgsImg from "../../image/health/ecosystem-managing-orgs.png";
import ecosystemReceivingCareImg from "../../image/health/ecosystem-receiving-care.png";
import ecosystemSafeDeploymentImg from "../../image/health/ecosystem-safe-deployment.png";
import ecosystemScalingEcosystemImg from "../../image/health/ecosystem-scaling-ecosystem.png";
import ecosystemTeachingCareImg from "../../image/health/ecosystem-teaching-care.png";
import { useLang } from "../Context";
import { StaggerInView, StaggerInViewItem, getHealthModulePanelMotion } from "../tools/Animations";
import { HealthSection } from "./HealthSection";
import { HealthSectionHeader } from "./HealthSectionHeader";
import { getLocalized, HealthLang, LocalizedString } from "./healthTypes";
import { healthCardBody, healthChipTealTag, healthFocusVisible } from "./healthVisualStyles";

interface HealthEcosystemMapSectionProps {
    data: DataKeys;
}

type EcosystemGroupId =
    | "receiving-care"
    | "delivering-care"
    | "teaching-care"
    | "managing-orgs"
    | "safe-deployment"
    | "scaling-ecosystem";

const GROUP_ICONS: Record<EcosystemGroupId, LucideIcon> = {
    "receiving-care": HeartPulse,
    "delivering-care": Stethoscope,
    "teaching-care": BookOpenText,
    "managing-orgs": Building2,
    "safe-deployment": ShieldCheck,
    "scaling-ecosystem": Handshake,
};

/** Hub-and-spoke node anchors (% of diagram box) — matches reference layout */
const NODE_ANCHORS: Record<EcosystemGroupId, { x: number; y: number }> = {
    "receiving-care": { x: 22, y: 18 },
    "delivering-care": { x: 78, y: 18 },
    "managing-orgs": { x: 82, y: 50 },
    "scaling-ecosystem": { x: 72, y: 82 },
    "safe-deployment": { x: 28, y: 82 },
    "teaching-care": { x: 18, y: 50 },
};

const LEFT_SIDE_GROUPS = new Set<EcosystemGroupId>([
    "receiving-care",
    "teaching-care",
    "safe-deployment",
]);

const ECOSYSTEM_GROUP_IDS = Object.keys(NODE_ANCHORS) as EcosystemGroupId[];

const HUB_CENTER_VB = { x: 50, y: 50 };
/** ~8.5rem hub radius mapped into 0–100 viewBox (not rem units) */
const HUB_RADIUS_VB = 13.2;

/** Space between Aimma hub edge and connector dot center (viewBox units) */
const HUB_CONNECTOR_DOT_GAP_VB = 2;

/** Fixed side-center attach points — mirrored left/right around hub */
const CONNECTOR_ENDPOINTS: Record<EcosystemGroupId, { x: number; y: number }> = {
    "receiving-care": { x: 31, y: 18 },
    "delivering-care": { x: 69, y: 18 },
    "teaching-care": { x: 27, y: 50 },
    "managing-orgs": { x: 73, y: 50 },
    "safe-deployment": { x: 37, y: 82 },
    "scaling-ecosystem": { x: 63, y: 82 },
};

type ConnectorGeometry = {
    paths: Record<EcosystemGroupId, string>;
    dots: Record<EcosystemGroupId, { x: number; y: number }>;
};

function hubDotForEndpoint(end: { x: number; y: number }): { x: number; y: number } {
    const dx = end.x - HUB_CENTER_VB.x;
    const dy = end.y - HUB_CENTER_VB.y;
    const len = Math.hypot(dx, dy) || 1;
    const offset = HUB_RADIUS_VB + HUB_CONNECTOR_DOT_GAP_VB;
    return {
        x: HUB_CENTER_VB.x + (dx / len) * offset,
        y: HUB_CENTER_VB.y + (dy / len) * offset,
    };
}

/** Smooth S-curve from hub perimeter to button side-center */
function buildConnectorPath(
    start: { x: number; y: number },
    end: { x: number; y: number },
    attachSide: "left" | "right"
): string {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const dist = Math.hypot(dx, dy);
    const bend = Math.min(dist * 0.38, 13);

    const verticalBand =
        end.y < 42 ? "top" : end.y > 58 ? "bottom" : "middle";
    const outward = attachSide === "right" ? 1 : -1;

    let c1x: number;
    let c1y: number;
    let c2x: number;
    let c2y: number;

    if (verticalBand === "top") {
        c1x = start.x + outward * bend * 0.15;
        c1y = start.y - bend * 0.55;
        c2x = end.x + outward * bend * 0.45;
        c2y = end.y + bend * 0.15;
    } else if (verticalBand === "bottom") {
        c1x = start.x + outward * bend * 0.15;
        c1y = start.y + bend * 0.55;
        c2x = end.x + outward * bend * 0.45;
        c2y = end.y - bend * 0.15;
    } else {
        c1x = start.x + outward * bend * 0.35;
        c1y = start.y + dy * 0.08;
        c2x = end.x + outward * bend * 0.55;
        c2y = end.y - dy * 0.06;
    }

    const fmt = (n: number) => n.toFixed(2);
    return `M ${fmt(start.x)} ${fmt(start.y)} C ${fmt(c1x)} ${fmt(c1y)}, ${fmt(c2x)} ${fmt(c2y)}, ${fmt(end.x)} ${fmt(end.y)}`;
}

const FIXED_CONNECTOR_GEOMETRY: ConnectorGeometry = ECOSYSTEM_GROUP_IDS.reduce(
    (acc, groupId) => {
        const end = CONNECTOR_ENDPOINTS[groupId];
        const dot = hubDotForEndpoint(end);
        const attachSide = LEFT_SIDE_GROUPS.has(groupId) ? "right" : "left";
        acc.paths[groupId] = buildConnectorPath(dot, end, attachSide);
        acc.dots[groupId] = dot;
        return acc;
    },
    { paths: {} as Record<EcosystemGroupId, string>, dots: {} as Record<EcosystemGroupId, { x: number; y: number }> }
);

type RoleLabelLayout = {
    multiline: LocalizedString[];
    singleLine: LocalizedString;
    /** Languages that use intentional two-line breaks (others use singleLine) */
    multilineLangs: HealthLang[];
};

const ROLE_LABEL_LAYOUT: Record<EcosystemGroupId, RoleLabelLayout> = {
    "receiving-care": {
        multiline: [
            { En: "Patients &", Zh: "", Jp: "" },
            { En: "Caregivers", Zh: "", Jp: "" },
        ],
        singleLine: { En: "Patients & Caregivers", Zh: "病人與照顧者", Jp: "患者と介護者" },
        multilineLangs: ["En"],
    },
    "delivering-care": {
        multiline: [
            { En: "Doctors &", Zh: "", Jp: "" },
            { En: "Nurses", Zh: "", Jp: "" },
        ],
        singleLine: { En: "Doctors & Nurses", Zh: "醫師與護理人員", Jp: "医師と看護職" },
        multilineLangs: ["En"],
    },
    "teaching-care": {
        multiline: [
            { En: "Medical Educators", Zh: "", Jp: "" },
            { En: "& Learners", Zh: "", Jp: "" },
        ],
        singleLine: {
            En: "Medical Educators & Learners",
            Zh: "醫學教育者與學習者",
            Jp: "医学教育者と学習者",
        },
        multilineLangs: ["En"],
    },
    "managing-orgs": {
        multiline: [
            { En: "Healthcare", Zh: "", Jp: "" },
            { En: "Leaders", Zh: "", Jp: "" },
        ],
        singleLine: { En: "Healthcare Leaders", Zh: "醫療機構領導者", Jp: "医療機関のリーダー" },
        multilineLangs: ["En"],
    },
    "safe-deployment": {
        multiline: [
            { En: "IT, Compliance &", Zh: "", Jp: "" },
            { En: "Governance", Zh: "", Jp: "" },
        ],
        singleLine: {
            En: "IT, Compliance & Governance",
            Zh: "IT、法遵與治理",
            Jp: "IT、コンプライアンス、ガバナンス",
        },
        multilineLangs: ["En"],
    },
    "scaling-ecosystem": {
        multiline: [
            { En: "Partners &", Zh: "", Jp: "" },
            { En: "Investors", Zh: "", Jp: "" },
        ],
        singleLine: { En: "Partners & Investors", Zh: "合作夥伴與投資人", Jp: "パートナーと投資家" },
        multilineLangs: ["En"],
    },
};

function getRoleLabelTextLines(groupId: EcosystemGroupId, hLang: HealthLang): string[] {
    const layout = ROLE_LABEL_LAYOUT[groupId];
    if (layout.multilineLangs.includes(hLang)) {
        return layout.multiline.map((line) => getLocalized(line, hLang));
    }
    return [getLocalized(layout.singleLine, hLang)];
}

const SELECT_HINT: LocalizedString = {
    En: "Select a group on the left to explore how Aimma supports their unique needs.",
    Zh: "點選左側群組，了解 Aimma 如何支援各角色的獨特需求。",
    Jp: "左側のグループを選択して、Aimmaがそれぞれのニーズをどのように支えるかをご覧ください。",
};

const HUB_SUBTITLE: LocalizedString = {
    En: "Connected care ecosystem",
    Zh: "連結的照護生態系",
    Jp: "連携するケアエコシステム",
};

const CLUSTER_LABEL: LocalizedString = {
    En: "Ecosystem cluster",
    Zh: "生態系群組",
    Jp: "エコシステムクラスター",
};

const ECOSYSTEM_ILLUSTRATIONS: Record<EcosystemGroupId, string> = {
    "receiving-care": ecosystemReceivingCareImg,
    "delivering-care": ecosystemDeliveringCareImg,
    "teaching-care": ecosystemTeachingCareImg,
    "managing-orgs": ecosystemManagingOrgsImg,
    "safe-deployment": ecosystemSafeDeploymentImg,
    "scaling-ecosystem": ecosystemScalingEcosystemImg,
};

function resolveGroupId(id?: string): EcosystemGroupId {
    if (id && id in NODE_ANCHORS) return id as EcosystemGroupId;
    return "receiving-care";
}

function EcosystemRoleButtonLabel({
    lines,
    isActive,
}: {
    lines: string[];
    isActive: boolean;
}) {
    const isMultiline = lines.length > 1;
    return (
        <span
            className={`min-w-0 text-xs leading-[1.35] sm:text-[13px] ${
                isActive ? "font-semibold text-teal-700" : "font-medium text-grey-700"
            }`}
        >
            {lines.map((text, lineIndex) => (
                <span
                    key={lineIndex}
                    className={
                        isMultiline ? "block whitespace-nowrap" : "whitespace-nowrap"
                    }
                >
                    {text}
                </span>
            ))}
        </span>
    );
}

function EcosystemAudienceIllustration({
    groupId,
    alt,
    centered = false,
}: {
    groupId: EcosystemGroupId;
    alt: string;
    centered?: boolean;
}) {
    return (
        <img
            src={ECOSYSTEM_ILLUSTRATIONS[groupId]}
            alt={alt}
            className={`h-auto w-full max-w-[360px] object-contain ${
                centered ? "mx-auto object-center" : "object-left"
            }`}
            loading="lazy"
            decoding="async"
        />
    );
}

export function HealthEcosystemMapSection({
    data,
}: HealthEcosystemMapSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const sectionId = "ecosystem-map";
    const eyebrow = getLocalized(data?.eyebrow, hLang);
    const title = getLocalized(data?.title, hLang);
    const subtitle = getLocalized(data?.subtitle, hLang);
    const groups: DataKeys[] = data?.groups ?? [];
    const [activeIndex, setActiveIndex] = useState(0);
    const reduceMotion = useReducedMotion();
    const activeGroup = groups[activeIndex] ?? groups[0];
    const activeGroupId = resolveGroupId(activeGroup?.id as string);
    const panelMotion = getHealthModulePanelMotion(1, reduceMotion);

    return (
        <HealthSection
            id={sectionId}
            ariaLabelledBy={`${sectionId}-title`}
            compact
            style={{ backgroundColor: "#FFFFFF" }}
        >
            <HealthSectionHeader
                title={title}
                titleId={`${sectionId}-title`}
                eyebrow={eyebrow}
                subtitle={subtitle}
                subtitleWide
            />

            {/* Desktop — hub-and-spoke (no outer card on left) */}
            <div className="hidden lg:grid lg:grid-cols-[minmax(0,0.56fr)_minmax(0,0.44fr)] lg:items-start lg:gap-10">
                <div className="relative h-[560px] w-full shrink-0">
                    <div className="absolute inset-x-0 top-0 h-[500px]">
                    <svg
                        className="pointer-events-none absolute inset-0 z-0 h-full w-full overflow-visible"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="xMidYMid meet"
                        aria-hidden="true"
                    >
                        {ECOSYSTEM_GROUP_IDS.map((id) => (
                            <path
                                key={id}
                                d={FIXED_CONNECTOR_GEOMETRY.paths[id]}
                                fill="none"
                                stroke="rgba(87,196,203,0.52)"
                                strokeWidth="0.22"
                                strokeDasharray="0.72 0.78"
                                strokeLinecap="round"
                            />
                        ))}
                    </svg>

                    <div className="absolute left-1/2 top-1/2 z-10 flex h-[8.5rem] w-[8.5rem] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border border-teal-100/90 bg-white text-center shadow-[0_16px_40px_rgba(3,124,132,0.14)]">
                        <span
                            className="text-xl leading-none text-teal-500"
                            aria-hidden="true"
                        >
                            ✦
                        </span>
                        <p className="mt-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-teal-600">
                            Aimma
                        </p>
                        <p className="mt-1 max-w-[7rem] text-center text-[11px] font-medium leading-snug text-grey-600">
                            {getLocalized(HUB_SUBTITLE, hLang)}
                        </p>
                    </div>

                    <svg
                        className="pointer-events-none absolute inset-0 z-[11] h-full w-full overflow-visible"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="xMidYMid meet"
                        aria-hidden="true"
                    >
                        {ECOSYSTEM_GROUP_IDS.map((id) => {
                            const dot = FIXED_CONNECTOR_GEOMETRY.dots[id];
                            return (
                                <circle
                                    key={`${id}-dot`}
                                    cx={dot.x}
                                    cy={dot.y}
                                    r="0.6"
                                    fill="#57C4CB"
                                    fillOpacity="0.68"
                                />
                            );
                        })}
                    </svg>

                    {groups.map((group, index) => {
                        const groupId = resolveGroupId(group.id as string);
                        const anchor = NODE_ANCHORS[groupId];
                        const isActive = index === activeIndex;
                        const labelLines = getRoleLabelTextLines(groupId, hLang);
                        const Icon = GROUP_ICONS[groupId];

                        return (
                            <button
                                key={group.id ?? index}
                                type="button"
                                onClick={() => setActiveIndex(index)}
                                aria-pressed={isActive}
                                aria-label={labelLines.join(" ")}
                                className={`absolute z-20 inline-flex w-max max-w-none -translate-x-1/2 -translate-y-1/2 items-center gap-2.5 rounded-full px-3 py-2.5 text-left transition-all duration-300 ${healthFocusVisible} ${
                                    isActive
                                        ? "border border-teal-300/80 bg-white shadow-[0_12px_28px_rgba(3,124,132,0.14)]"
                                        : "border border-transparent bg-white/90 shadow-[0_6px_18px_rgba(16,24,40,0.05)] hover:border-teal-100/80 hover:shadow-[0_10px_24px_rgba(3,124,132,0.1)]"
                                }`}
                                style={{
                                    left: `${anchor.x}%`,
                                    top: `${anchor.y}%`,
                                }}
                            >
                                <span
                                    className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all ${
                                        isActive
                                            ? "bg-[rgba(3,124,132,0.14)] text-teal-500 shadow-[0_0_0_6px_rgba(3,124,132,0.1)]"
                                            : "bg-[rgba(3,124,132,0.06)] text-teal-500/80"
                                    }`}
                                    aria-hidden="true"
                                >
                                    <Icon className="h-[17px] w-[17px]" strokeWidth={1.75} />
                                </span>
                                <EcosystemRoleButtonLabel
                                    lines={labelLines}
                                    isActive={isActive}
                                />
                            </button>
                        );
                    })}
                    </div>

                    <p className="absolute inset-x-0 bottom-3 flex w-full items-start justify-start gap-2 text-left text-sm leading-relaxed text-grey-500 [text-align:left]">
                        <MousePointerClick
                            className="mt-0.5 h-4 w-4 shrink-0 text-teal-500"
                            strokeWidth={1.75}
                            aria-hidden="true"
                        />
                        <span className="min-w-0 flex-1 text-left [text-align:left]">
                            {getLocalized(SELECT_HINT, hLang)}
                        </span>
                    </p>
                </div>

                <div className="min-w-0 w-full">
                    <div className="overflow-hidden rounded-[2rem] bg-white p-8 text-left shadow-[0_18px_48px_rgba(16,24,40,0.08)] transition-shadow duration-300 [text-align:left]">
                        <AnimatePresence mode="wait" initial={false}>
                            {activeGroup && (
                                <motion.div
                                    key={activeGroupId}
                                    initial={panelMotion.initial}
                                    animate={panelMotion.animate}
                                    exit={panelMotion.exit}
                                    transition={panelMotion.transition}
                                    className="w-full min-w-0 text-left [text-align:left]"
                                >
                                    <div className="flex items-center gap-3 text-left [text-align:left]">
                                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-teal-500">
                                            {getLocalized(CLUSTER_LABEL, hLang)}
                                        </p>
                                        <span
                                            className="h-px w-10 bg-teal-300/70"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <h3 className="mt-3 text-left text-xl font-semibold leading-snug text-grey-900 [text-align:left]">
                                        {getLocalized(activeGroup?.title, hLang)}
                                    </h3>
                                    <p
                                        className={`${healthCardBody} mt-4 text-left text-grey-600 [text-align:left]`}
                                    >
                                        {getLocalized(activeGroup?.summary, hLang)}
                                    </p>

                                    <div className="mt-8 w-full text-left">
                                        <EcosystemAudienceIllustration
                                            groupId={activeGroupId}
                                            alt={getLocalized(
                                                activeGroup?.title,
                                                hLang
                                            )}
                                        />
                                    </div>

                                    <div className="mt-6 flex flex-wrap justify-start gap-2 text-left [text-align:left]">
                                        {(activeGroup?.examples ?? []).map(
                                            (
                                                example: DataKeys,
                                                index: number
                                            ) => (
                                                <span
                                                    key={index}
                                                    className={healthChipTealTag}
                                                >
                                                    {getLocalized(
                                                        example as LocalizedString,
                                                        hLang
                                                    )}
                                                </span>
                                            )
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>

            {/* Mobile — stacked cards, centered layout */}
            <StaggerInView className="flex w-full min-w-0 flex-col gap-4 lg:hidden">
                {groups.map((group, index) => (
                    <StaggerInViewItem key={group.id ?? index} direction="bottom">
                        <div className="rounded-[1.5rem] border border-grey-100 bg-white p-5 text-center shadow-[0_8px_24px_rgba(16,24,40,0.05)]">
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-teal-500">
                                {getLocalized(CLUSTER_LABEL, hLang)}
                            </p>
                            <h3 className="mt-3 text-base font-semibold leading-snug text-grey-900 sm:text-lg">
                                {getLocalized(group?.title, hLang)}
                            </h3>
                            <p className={`${healthCardBody} mt-3 !text-center text-grey-600`}>
                                {getLocalized(group?.summary, hLang)}
                            </p>
                            <div className="mt-5 w-full">
                                <EcosystemAudienceIllustration
                                    groupId={resolveGroupId(group.id as string)}
                                    alt={getLocalized(group?.title, hLang)}
                                    centered
                                />
                            </div>
                            <div className="mt-4 flex flex-wrap justify-center gap-2">
                                {(group?.examples ?? []).map(
                                    (example: DataKeys, exampleIndex: number) => (
                                        <span
                                            key={exampleIndex}
                                            className={healthChipTealTag}
                                        >
                                            {getLocalized(
                                                example as LocalizedString,
                                                hLang
                                            )}
                                        </span>
                                    )
                                )}
                            </div>
                        </div>
                    </StaggerInViewItem>
                ))}
            </StaggerInView>
        </HealthSection>
    );
}
